import Fuse from "fuse.js";

/**
 * Generic text matching for cmdk command lists (used by the activity picker,
 * but nothing here is activity-specific).
 *
 * Callers give each item its human-readable name(s) as cmdk `keywords` and let
 * this module do the matching, so the list is filtered by what the user sees
 * rather than by any raw enum value.
 *
 * The ranking algorithm is isolated behind a single {@link TextMatcher} so it
 * can be fine-tuned or swapped wholesale without touching the components.
 */

/**
 * Scores how well `query` matches `candidate`.
 * Returns 0 for no match and a value in (0, 1] otherwise, where higher ranks first.
 * An empty query matches everything (score 1) so the full list is shown by default.
 */
export type TextMatcher = (candidate: string, query: string) => number;

/**
 * Normalizes text for comparison: case-folds, strips combining diacritics
 * (so "Café" ≈ "cafe") and drops whitespace/punctuation (so "Lø pi" ≈ "Løpi").
 * Distinct Nordic letters that don't decompose under NFD (ø, æ) are preserved,
 * so "ø" and "o" remain different — swap this out if you want them folded.
 */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

// Lower = stricter. 0.4 tolerates a typo or two without matching unrelated words.
const FUSE_THRESHOLD = 0.4;

/**
 * Default matcher: Fuse.js fuzzy (edit-distance) matching over the normalized
 * text, so small spelling mistakes and transpositions still match ("hånbdall" →
 * "Håndball"). We run Fuse per candidate because cmdk scores one item at a time;
 * the activity list is tiny, so the per-call index build is negligible.
 */
export const matchText: TextMatcher = (candidate, query) => {
  const q = normalize(query);
  if (q.length === 0) return 1;

  const c = normalize(candidate);
  if (c.length === 0) return 0;

  const fuse = new Fuse([c], {
    includeScore: true,
    ignoreLocation: true, // match anywhere in the string, not just the start
    threshold: FUSE_THRESHOLD,
  });
  const [best] = fuse.search(q);
  if (!best) return 0;

  // Fuse score is 0 (perfect) … 1 (worst); invert so higher = better for cmdk.
  return 1 - (best.score ?? 0);
};

export type TextFilterOptions = {
  /** Scoring function to rank matches. */
  matcher: TextMatcher;
  /**
   * Whether to match against every keyword a caller supplies, or only the first.
   *
   * Items pass their names as cmdk `keywords`, primary name first followed by any
   * aliases (e.g. the activity picker passes `[localizedName, englishName]`).
   * With `matchAliases: true` a cross-language search hits the English alias too;
   * with `false` only the primary (localized) name is searchable. Components
   * always supply the aliases — this flag alone decides if they count.
   */
  matchAliases: boolean;
};

/**
 * Builds a `filter` for cmdk's `Command` root. cmdk calls it with the item's
 * `value` plus its `keywords`; we score the keywords (the display name(s)) when
 * present, falling back to the value for items without any.
 */
export function createTextFilter({ matcher, matchAliases }: TextFilterOptions) {
  return (value: string, search: string, keywords?: string[]): number => {
    const haystack =
      keywords && keywords.length > 0
        ? matchAliases
          ? keywords
          : keywords.slice(0, 1)
        : [value];
    return haystack.reduce(
      (best, field) => Math.max(best, matcher(field, search)),
      0,
    );
  };
}
